/**
 * Programmatic SEO landing page — /transport/from-:from/to-:to
 *
 * Auto-generates a SEO-indexed landing page for every realistic
 * cross-border transport route. Slugs (e.g. "mexico", "usa", "uk")
 * map to display names + courier persona ("intl_transporter"). Google
 * indexes one page per route → free organic traffic to the intl
 * directory and to the request form.
 *
 * Renders with full Helmet meta + structured data so it ranks well on
 * narrow searches like "ship dog from mexico to usa" where the
 * organic competition is sparse.
 */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { AirplaneIcon as Airplane, ShieldCheckIcon as ShieldCheck, HeartIcon as Heart, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

// Curated set of country slugs we generate pages for. Each must
// exist for both `from` AND `to` to be valid; invalid slug
// combinations 404 so we don't pollute Google with thin pages.
const COUNTRIES = {
  usa: { name: "the USA", flag: "🇺🇸", domestic_pair: ["mexico", "canada", "uk", "germany", "spain", "italy", "france", "brazil", "australia", "japan"] },
  mexico: { name: "Mexico", flag: "🇲🇽", domestic_pair: ["usa"] },
  canada: { name: "Canada", flag: "🇨🇦", domestic_pair: ["usa"] },
  uk: { name: "the UK", flag: "🇬🇧", domestic_pair: ["usa"] },
  germany: { name: "Germany", flag: "🇩🇪", domestic_pair: ["usa"] },
  spain: { name: "Spain", flag: "🇪🇸", domestic_pair: ["usa"] },
  italy: { name: "Italy", flag: "🇮🇹", domestic_pair: ["usa"] },
  france: { name: "France", flag: "🇫🇷", domestic_pair: ["usa"] },
  brazil: { name: "Brazil", flag: "🇧🇷", domestic_pair: ["usa"] },
  australia: { name: "Australia", flag: "🇦🇺", domestic_pair: ["usa"] },
  japan: { name: "Japan", flag: "🇯🇵", domestic_pair: ["usa"] },
};

export default function TransportRoutePage() {
  const { from, to } = useParams();
  const fromCountry = COUNTRIES[from];
  const toCountry = COUNTRIES[to];

  // Invalid slug combo: 404 (returning null forces NotFound boundary)
  if (!fromCountry || !toCountry || from === to) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="font-spectral text-3xl mb-3">Route not found</h1>
          <p className="text-[#6B6B6B] mb-6">We don&rsquo;t have a landing page for this route yet.</p>
          <Link to="/transport/international" className="text-[#C1272D] underline">Browse all international couriers →</Link>
        </div>
      </AppShell>
    );
  }

  const isImporting = to === "usa";
  const title = `Ship a Dog from ${fromCountry.name} to ${toCountry.name} — Verified Intl Couriers · Armstrong K9 Registry`;
  const desc = `Verified international dog couriers operating the ${fromCountry.name} → ${toCountry.name} route. Compare quotes, see safety records, request transport directly. No escrow held — direct buyer-to-courier introduction with venue-only liability posture.`;
  const url = `https://armstrongk9registry.com/transport/route/${from}/${to}`;

  return (
    <AppShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": `International dog transport: ${fromCountry.name} to ${toCountry.name}`,
          "provider": { "@type": "Organization", "name": "Armstrong K9 Registry" },
          "serviceType": "International dog courier",
          "areaServed": [fromCountry.name, toCountry.name],
        })}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid={`transport-route-page-${from}-${to}`}>
        <Link to="/transport/international" className="text-[12px] text-[#6B6B6B] hover:text-ink">
          ← All international couriers
        </Link>
        <div className="mt-4 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark">Verified courier route</span>
        </div>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight leading-tight mb-3" data-testid="route-h1">
          Ship a dog from <span className="whitespace-nowrap">{fromCountry.flag} {fromCountry.name}</span> to <span className="whitespace-nowrap">{toCountry.flag} {toCountry.name}</span>
        </h1>
        <p className="text-[15px] text-[#3F3F3F] leading-relaxed max-w-2xl mb-7">
          Find verified, click-wrapped international dog couriers running the
          {" "}<b>{fromCountry.name} → {toCountry.name}</b> route. Each courier is
          identity-verified and operates under Armstrong&rsquo;s strict venue posture —
          we hand you the introduction; the courier handles the trip and collects
          payment directly. <b>No escrow, no held funds, no surprise customs liability.</b>
        </p>

        {/* Trust strip */}
        <div className="flex flex-wrap gap-3 mb-8 text-[12px] text-[#6B6B6B]">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <ShieldCheck size={14} weight="duotone" className="text-emerald-600" />
            Verified couriers only
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <Heart size={14} weight="duotone" className="text-crimson" />
            Click-wrapped carriage attestation
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <Airplane size={14} weight="duotone" className="text-ink" />
            Cross-border routes
          </span>
        </div>

        {/* Primary CTAs */}
        <div className="flex flex-wrap gap-3 mb-12">
          <Link to={`/transport/international?from=${from}&to=${to}`}>
            <Button className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-7 text-[14px] font-semibold" data-testid="route-browse-couriers">
              Browse couriers on this route
              <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </Link>
          <Link to={`/transport/international/apply?route=${from}-${to}`}>
            <Button variant="outline" className="rounded-full h-12 px-7 text-[14px] font-semibold" data-testid="route-i-am-a-courier">
              I&rsquo;m a courier on this route
            </Button>
          </Link>
        </div>

        {/* SEO content blocks — give Google something to index */}
        <div className="space-y-8 text-[15px] leading-relaxed text-[#3F3F3F]">
          <section>
            <h2 className="font-spectral text-2xl text-ink mb-3">What you get on the {fromCountry.name} → {toCountry.name} route</h2>
            <ul className="space-y-2 list-disc pl-5 marker:text-gold">
              <li><b>Verified introductions only.</b> Every courier has paid the $9 verification fee and accepted the carriage-liability click-wrap.</li>
              <li><b>Pay your courier directly.</b> Armstrong never escrows international funds &mdash; quote, contract, and payment all happen between you and the courier off-platform.</li>
              <li><b>Trip update timeline.</b> Once the trip is underway, you receive lagged-GPS milestones + EXIF-stripped photos via a token-gated page (no risk to courier safety).</li>
              <li><b>$9 verified intro fee.</b> Filters tire-kickers; captures your email for dispute mediation if anything goes wrong.</li>
              <li><b>Trust-ladder ranking.</b> Couriers with more buyer-confirmed deliveries rank higher in directory results &mdash; the platform self-cleans.</li>
            </ul>
          </section>

          {isImporting && (
            <section>
              <h2 className="font-spectral text-2xl text-ink mb-3">Importing into the USA — what to expect</h2>
              <p className="mb-2">U.S. CDC rules apply to all dog imports. Your courier should be familiar with current health certificate, microchip, and rabies-titer requirements for dogs originating from {fromCountry.name}.</p>
              <p>Armstrong does not provide customs or veterinary advice &mdash; we are the introduction service. Confirm requirements directly with your courier and with the <a href="https://www.cdc.gov/importation/bringing-an-animal-into-the-united-states/index.html" target="_blank" rel="noopener noreferrer" className="text-[#C1272D] underline">CDC import guidance</a> before booking.</p>
            </section>
          )}

          <section>
            <h2 className="font-spectral text-2xl text-ink mb-3">How the introduction works</h2>
            <ol className="space-y-2 list-decimal pl-5 marker:text-gold">
              <li>Click <b>&ldquo;Browse couriers on this route&rdquo;</b> above &mdash; you&rsquo;ll see verified pros filtered to {fromCountry.name} → {toCountry.name}.</li>
              <li>Pick a courier whose profile fits your dog and trip dates.</li>
              <li>Send a <b>Verified Inquiry</b> ($9 one-time intro fee) &mdash; Armstrong routes your message via email relay.</li>
              <li>The courier replies directly to your email with a quote and timeline.</li>
              <li>You settle terms, payment, and pickup with the courier directly &mdash; off-platform.</li>
            </ol>
          </section>
        </div>

        <div className="mt-14 pt-8 border-t border-[#EAEAEA]">
          <h2 className="font-spectral text-2xl text-ink mb-3">Other popular routes</h2>
          <div className="flex flex-wrap gap-2">
            {Object.keys(COUNTRIES)
              .filter((k) => k !== from && k !== to)
              .slice(0, 8)
              .map((k) => (
                <Link
                  key={k}
                  to={`/transport/route/${isImporting ? k : "usa"}/${isImporting ? "usa" : k}`}
                  className="rounded-full border border-[#EAEAEA] hover:border-ink px-3 py-1.5 text-[12px] text-[#6B6B6B] hover:text-ink transition-colors"
                  data-testid={`route-related-${k}`}
                >
                  {COUNTRIES[k].flag} {COUNTRIES[k].name} {isImporting ? "→ USA" : "← USA"}
                </Link>
              ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// Expose the country list so the sitemap generator + tests can
// enumerate every valid route page.
export const SEO_TRANSPORT_ROUTES = Object.keys(COUNTRIES).flatMap((from) =>
  COUNTRIES[from].domestic_pair.map((to) => ({ from, to }))
);
